// =====================================================================
// SERVICE: quiz-api.service.ts
// FUNCTIONAL OVERVIEW:
//   Data/API layer for the Quiz REST endpoints (knowledge-check-service):
//   create session, read session, submit batch answers, continue with the
//   next batch, complete, history, chat-history turns and export. Thin HttpClient wrappers
//   that map the backend v1 wire shapes (nested `session`, limit/offset
//   paging) to the UI DTOs – state and error handling live in QuizFacade.
//   Auth is added by AuthInterceptor; requests skip the global spinner
//   because the quiz screen shows its own inline loading states.
// DEPENDENCIES: HttpClient, API_URLS.QUIZ
// =====================================================================
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_URLS } from '../../constants/api-urls/api-urls';
import {
  QuizAnswerSubmission,
  QuizAnswerSubmissionResponse,
  QuizBatchResponseV1,
  QuizBatchWire,
  QuizChatHistoryQuery,
  QuizChatHistoryResponse,
  QuizCompleteResponse,
  QuizCompleteWire,
  QuizContinueRequest,
  QuizCreateSessionRequest,
  QuizCreateSessionResponse,
  QuizDetailWire,
  QuizExportRequest,
  QuizExportResponse,
  QuizHistoryQuery,
  QuizHistoryResponse,
  QuizSessionDetail,
  QuizSessionWire,
  QuizSubmitAnswersWire,
} from '../../models/quiz/quiz-api.models';
import { QUIZ_BATCH_SIZE } from '../../modules/quiz/constants/quiz.constants';

const toSessionDetail = (session: QuizSessionWire, extra: Partial<QuizSessionDetail> = {}): QuizSessionDetail => ({
  quiz_session_id: session.quiz_session_id,
  chat_session_id: session.chat_session_id,
  title: session.topic_prompt ?? undefined,
  request_type: session.request_type,
  scope_mode: session.scope_mode,
  generation_mode: session.generation_mode,
  status: session.status,
  current_batch_no: session.current_batch_no,
  total_questions: session.total_questions_generated,
  completed_at: session.completed_at ?? null,
  read_only: session.read_only,
  questions: [],
  ...extra,
});

const toBatch = (wire: QuizBatchWire): QuizBatchResponseV1 => ({
  quiz_session_id: wire.session.quiz_session_id,
  batch_no: wire.batch_no,
  returned_count: wire.returned_count,
  items: wire.items ?? [],
});

const toDetail = (wire: QuizDetailWire): QuizSessionDetail =>
  toSessionDetail(wire.session, { questions: wire.questions ?? [], summary: wire.summary ?? undefined });

@Injectable({
  providedIn: 'root',
})
export class QuizApiService {
  private readonly http = inject(HttpClient);

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Creates a quiz session (and its first batch when auto_generate).
  //   With files, sends multipart: `payload` (JSON) + `uploaded_documents`.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  createSession(request: QuizCreateSessionRequest, files: File[] = []): Observable<QuizCreateSessionResponse> {
    const body = { requested_count: QUIZ_BATCH_SIZE, ...request };
    if (!files.length) {
      return this.http.post<QuizCreateSessionResponse>(API_URLS.QUIZ.SESSIONS, body);
    }
    const formData = new FormData();
    formData.append('payload', JSON.stringify(body));
    files.forEach((file) => formData.append('uploaded_documents', file, file.name));
    return this.http.post<QuizCreateSessionResponse>(API_URLS.QUIZ.SESSIONS, formData);
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Loads a full quiz session (used for resync of the active quiz).
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-26 12:00 | Vikas Kumar | Map backend { session, summary, questions } response.
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  getSession(quizSessionId: number): Observable<QuizSessionDetail> {
    return this.http.get<QuizDetailWire>(API_URLS.QUIZ.SESSION(quizSessionId)).pipe(map(toDetail));
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Loads a quiz from history (read-only review).
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-26 12:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  getHistoryDetail(quizSessionId: number): Observable<QuizSessionDetail> {
    return this.http.get<QuizDetailWire>(API_URLS.QUIZ.HISTORY_DETAIL(quizSessionId)).pipe(map(toDetail));
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Submits the attempted answers of the active batch in one call.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-26 12:00 | Vikas Kumar | Map backend session to the UI session DTO.
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  submitAnswers(quizSessionId: number, submission: QuizAnswerSubmission): Observable<QuizAnswerSubmissionResponse> {
    return this.http
      .post<QuizSubmitAnswersWire>(API_URLS.QUIZ.ANSWERS(quizSessionId), submission)
      .pipe(map((wire) => ({ session: toSessionDetail(wire.session), results: wire.results ?? [] })));
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Requests the next batch; the backend guarantees no repeats.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-26 12:00 | Vikas Kumar | Map backend batch (quiz_session_id from nested session).
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  continueQuestions(quizSessionId: number, requestedCount: number = QUIZ_BATCH_SIZE): Observable<QuizBatchResponseV1> {
    const body: QuizContinueRequest = { requested_count: requestedCount };
    return this.http.post<QuizBatchWire>(API_URLS.QUIZ.CONTINUE(quizSessionId), body).pipe(map(toBatch));
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Ends the quiz and returns the final summary.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-26 12:00 | Vikas Kumar | Map backend { session, summary, questions } response.
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  completeSession(quizSessionId: number): Observable<QuizCompleteResponse> {
    return this.http.post<QuizCompleteWire>(API_URLS.QUIZ.COMPLETE(quizSessionId), {}).pipe(
      map((wire) => ({
        quiz_session_id: wire.session.quiz_session_id,
        summary: wire.summary,
        questions: wire.questions ?? [],
      }))
    );
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Loads the quiz chats of the user (sidebar history), grouped like the
  //   chat history (today, current week, last week, one month ago).
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-29 21:00 | Vikas Kumar | Chat-session grouped history (group_by, business unit, region).
  //   [JHUD-5314] 2026-10-26 12:00 | Vikas Kumar | Switch to backend limit/offset paging and map session items.
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  getHistory(query: QuizHistoryQuery): Observable<QuizHistoryResponse> {
    let params = new HttpParams().set('group_by', query.group_by).set('business_unit_code', query.business_unit_code);
    if (query.region_name) {
      params = params.set('region_name', query.region_name);
    }
    if (query.offset != null) {
      params = params.set('offset', query.offset);
    }
    return this.http.get<QuizHistoryResponse>(API_URLS.QUIZ.HISTORY, { params });
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Loads one page of the quiz chat turns (ledger) used to replay a quiz
  //   conversation on the history page.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-29 20:16 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  getChatHistory(query: QuizChatHistoryQuery): Observable<QuizChatHistoryResponse> {
    let params = new HttpParams();
    if (query.chat_session_id != null) {
      params = params.set('chat_session_id', query.chat_session_id);
    }
    if (query.quiz_session_id != null) {
      params = params.set('quiz_session_id', query.quiz_session_id);
    }
    if (query.limit != null) {
      params = params.set('limit', query.limit);
    }
    if (query.offset != null) {
      params = params.set('offset', query.offset);
    }
    if (query.order) {
      params = params.set('order', query.order);
    }
    return this.http.get<QuizChatHistoryResponse>(API_URLS.QUIZ.CHAT_HISTORY, { params });
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Creates an export record (DOCX or PDF) for a quiz session.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  createExport(quizSessionId: number, request: QuizExportRequest): Observable<QuizExportResponse> {
    return this.http.post<QuizExportResponse>(API_URLS.QUIZ.EXPORTS(quizSessionId), request);
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Reads export status (used to poll until the document is ready).
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5314] 2026-10-26 12:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  getExport(exportId: number): Observable<QuizExportResponse> {
    return this.http.get<QuizExportResponse>(API_URLS.QUIZ.EXPORT(exportId));
  }
}

// =====================================================================
// NOTE TO USER – INFORMATION / CONFIRMATION REQUIRED:
//   1. Upload contract: knowledge-check v1 accepts JSON only and expects
//      `uploaded_documents[].filename` (search-index key `<wwid>_<filename>`)
//      of a document already uploaded elsewhere. The multipart path below
//      `createSession(..., files)` does not match yet – confirm the upload
//      flow before relying on it.
// =====================================================================
